import {
  channelRouteOf,
  formatRoute,
  isVideoId,
  parseRoute,
  playlistEditable,
  playlistEntryAuthorMissing,
  playlistEntryFacts,
  playlistEntryUnavailable,
  playlistScrape,
  playlistUpdatedEpochSec,
  playlistVisibility,
  PLAYLIST_REORDER_SUPPORTED,
  retargetVideoFrameUrl,
  pickImage,
  type FeedItem,
  type Playlist,
  type PlaylistListing,
  type PlaylistPrivacy,
  type VideoDetails,
  type VideoSummary,
} from '@visious/core';
import { describeVisibility, type VisibilityCopy } from '../../playlists/model';
import { describeProblem, type ProblemCopy } from '../../api/problem';
import type { ApiProblem } from '../../api/videos';
import { formatDuration } from '../../format';
import { fmt, text } from '../../strings';
import {
  isWatchedVideo,
  matchesGridSearch,
  toListingProgress,
  NO_PROGRESS,
  type ListingProgress,
  type ProgressLookup,
} from '../../listing/model';
import type { QueueCandidate } from '../../queue/model';
import { toRichNodeViews, type RichNodeView } from '../../richText/model';
import type { MenuModel } from '../../ui/menuModel';

/**
 * §8's Playlist page, as reasoning — R13: no DOM, no React, no `RefObject`.
 * `usePlaylist.ts` feeds it and `view.tsx` renders what it returns.
 *
 * This screen holds **no state at all** beyond the pages loaded, the same as 2c's and 2d's: the id is
 * in the URL and there is nothing else to choose. The endpoint takes no sort, no region and no filter.
 *
 * The editing half was added later, and one thing 2e wrote in advance turned out to matter more than it
 * looked: nothing here keys anything on `index`, because removal keys on `indexId`. That is now true for
 * a second, sharper reason — removing an entry makes the server **renumber every later `index`**, so a
 * position is not even stable within one playlist, let alone a key. The position is for *display*.
 */

/** 320 for a list row at 2×, for 2b's reason — 320×180 at 9–15 KB or 1280×720 at 102 KB, nothing between. */
const ROW_THUMBNAIL_MIN_WIDTH = 320;

/** The narrowest avatar that covers the header's 48 px box at 2×. `authorThumbnails[]` sorts ascending. */
const AVATAR_MIN_WIDTH = 176;

/* ------------------------------------------------------------------------------------------------
 * The header
 * ---------------------------------------------------------------------------------------------- */

export interface PlaylistHeader {
  readonly playlistId: string;
  readonly title: string;
  readonly author: string;
  readonly authorHref: string | null;
  readonly avatarUrl: string | null;
  readonly thumbnailUrl: string | null;
  /**
   * What the header says beside the title. Each one is `null` rather than zero when the API did not
   * serve it — 2d's About tab lesson, which is that a zero printed as a figure is a claim.
   */
  readonly videoCountText: string | null;
  readonly viewCountText: string | null;
  readonly viewCountTitle: string | null;
  /**
   * `null` when `updated` is not a date. It is **day-granular** for a mirrored YouTube playlist (18 of
   * 20 values exactly midnight UTC), and a playlist Invidious had just refreshed reported the request's
   * own second — once **6 seconds into the future** — so the relative form would read "in 6 seconds"
   * about a playlist last touched in 2018.
   */
  readonly updatedText: string | null;
  readonly updatedTitle: string | null;
  /** `true` for an `IVPL…` id: the only thing that says whether the mutation endpoints will apply. */
  readonly invidiousOwned: boolean;
  readonly isListed: boolean;
  /**
   * Whether to draw the editing affordances at all. A session, **and** a playlist this account can
   * write to (`playlistEditable`): a mirrored YouTube playlist answers every change with the `404` a
   * deleted one gets, and another account's native playlist refuses too, so offering a control that
   * fails either way would be indistinguishable from the app being broken.
   */
  readonly canEdit: boolean;
  /** What the header may say about who can see this — two states, not three. See `describeVisibility`. */
  readonly visibility: VisibilityCopy;
  /** There is no reorder endpoint, so the page states that rather than leaving a reader looking. */
  readonly reorderSupported: boolean;
  readonly descriptionNodes: readonly RichNodeView[];
  readonly hasDescription: boolean;
  /**
   * `/watch?v=<first playable entry>&list=<id>&index=<its position>` — or `null` when nothing in the
   * playlist can be played. Built from the first entry whose video is **not** blanked: a playlist whose
   * first entry is a deleted video would otherwise offer a Play button that lands on "Video not found".
   */
  readonly playAllHref: string | null;
}

export function toPlaylistHeader(
  playlist: Playlist,
  listing: PlaylistListing,
  nowEpochSec: number,
  editing: {
    readonly canEdit: boolean;
    readonly viewerUsername: string | null;
    readonly knownPrivacy: PlaylistPrivacy | null;
  } = {
    canEdit: false,
    viewerUsername: null,
    knownPrivacy: null,
  },
): PlaylistHeader {
  const route = channelRouteOf(playlist);
  const updated = playlistUpdatedEpochSec(playlist, nowEpochSec);
  const firstPlayable = listing.entries.find((entry) => !playlistEntryUnavailable(entry.video));

  return {
    playlistId: playlist.playlistId,
    title: playlist.title,
    author: playlist.author,
    // `channelRouteOf` rather than "prefer authorId, else authorUrl": an Invidious-native playlist
    // carries neither, and a mirrored one can carry `authorUrl: "/channel/"` — a non-empty URL that
    // routes nowhere (`links/routes.ts`).
    authorHref: route === null ? null : formatRoute(route),
    avatarUrl: pickImage(playlist.authorThumbnails, { minWidth: AVATAR_MIN_WIDTH })?.url ?? null,
    /*
     * `retargetVideoFrameUrl` for the same reason 2c's playlist card needs it: `playlistThumbnail` is a
     * single URL rather than a size list, and the one it hands over is `hq720.jpg` at **275 KB**. The
     * header box is bigger than a card's, so this asks for `mqdefault` rather than the smallest — and it
     * leaves the `/pl_c/` form untouched, where the `sqp` query *is* the address.
     */
    thumbnailUrl:
      playlist.thumbnail === null ? null : retargetVideoFrameUrl(playlist.thumbnail.url, 'mqdefault'),
    // Pluralised on the number, not the label: `formatCount` compacts, so a view holding "1" cannot
    // tell it from "21" — which is how a one-entry playlist read "1 videos" (`Formatters.countedNoun`).
    videoCountText:
      playlist.videoCount === null || playlist.videoCount <= 0
        ? null
        : fmt().countedNoun(playlist.videoCount, text().playlists.video),
    viewCountText:
      playlist.viewCount === null || playlist.viewCount <= 0 ? null : fmt().count(playlist.viewCount),
    viewCountTitle:
      playlist.viewCount === null || playlist.viewCount <= 0
        ? null
        : fmt().distinctExactTitle(
            fmt().count(playlist.viewCount),
            fmt().exactCount(playlist.viewCount),
          ),
    updatedText: fmt().published(updated, nowEpochSec),
    // An absolute tooltip is honest here, unlike a search row's date: `updated` is a real day rather
    // than a value back-computed from coarse relative text.
    updatedTitle: fmt().absoluteDate(updated),
    invidiousOwned: playlist.invidiousOwned,
    isListed: playlist.isListed,
    canEdit: editing.canEdit && playlistEditable(playlist, editing.viewerUsername),
    visibility: describeVisibility(playlistVisibility(playlist, editing.knownPrivacy)),
    reorderSupported: PLAYLIST_REORDER_SUPPORTED,
    descriptionNodes: toRichNodeViews(playlist.descriptionNodes),
    hasDescription: playlist.descriptionNodes.length > 0,
    playAllHref:
      firstPlayable === undefined
        ? null
        : formatRoute({
            kind: 'watch',
            videoId: firstPlayable.video.videoId,
            startSec: null,
            playlistId: playlist.playlistId,
            index: firstPlayable.index,
          }),
  };
}

/* ------------------------------------------------------------------------------------------------
 * The entry list
 * ---------------------------------------------------------------------------------------------- */

/** The corner of a thumbnail. Never `live` on this endpoint — see {@link playlistEntryFacts}. */
export interface EntryBadge {
  readonly kind: 'duration' | 'upcoming';
  readonly text: string;
  readonly title: string | null;
}

/**
 * One card of the entry grid.
 *
 * A grid of feed-shaped cards, but the position survives the change of clothes: it
 * is the one piece of information a playlist row carries that a feed row does not, and it is what
 * makes "the fourteenth video" a thing a reader can point at — it rides the thumbnail's corner.
 * `toListingItems` is not reused because two of its five fields (the view count and the date) are
 * structurally absent here and a third (the badge) needs the per-endpoint correction.
 */
export type PlaylistEntryRow =
  | {
      readonly kind: 'video';
      readonly key: string;
      /** 1-based, for display. Derived from the API's own `index`, so it survives the dedupe fold. */
      readonly position: number;
      readonly videoId: string;
      /**
       * The selection and removal key — Invidious's own, stable across removals. `null` on a
       * mirrored YouTube playlist's rows, which cannot be changed anyway (`canEdit` is false there).
       */
      readonly indexId: string | null;
      readonly selected: boolean;
      /** What the manage checkbox says it selects — a title, so a screen reader hears which card. */
      readonly selectLabel: string;
      readonly href: string;
      readonly title: string;
      /** `null` when the endpoint returned the row without its channel — 892 of 8 101 rows measured. */
      readonly author: string | null;
      readonly authorHref: string | null;
      readonly thumbnailUrl: string | null;
      readonly badge: EntryBadge | null;
      /**
       * The reported length, `null` where the endpoint gave none. Read only by the hover preview,
       * as the clamp against the storyboard's blank tail (`useGridPreview`); what the corner of the
       * frame *shows* is the badge, which went through the per-endpoint correction.
       */
      readonly durationSec: number | null;
      /** 4b's bar, from the local playhead store. `null` when nothing is stored for this entry. */
      readonly progress: ListingProgress | null;
      /** The watched fade and the thumbnail's "Watched" mark — every grid's one line (`isWatchedVideo`). */
      readonly watched: boolean;
    }
  | {
      /**
       * The video is gone — deleted, private, terminated or region-blocked — and the endpoint blanks
       * the row rather than omitting it (6.9 % of rows measured). Kept in the list so the numbering
       * stays truthful, and **not a link**: the id is real, so an anchor would look live and land on
       * "Video not found". Selectable in manage mode — a dead entry is exactly what a reader wants
       * out of their playlist, and `indexId` is Invidious's key, nothing to do with YouTube.
       */
      readonly kind: 'unavailable';
      readonly key: string;
      readonly position: number;
      readonly videoId: string;
      readonly indexId: string | null;
      readonly selected: boolean;
      readonly selectLabel: string;
    };

/**
 * An entry as the queue's two controls see it (`queue/model.ts`), with whatever title the card is
 * showing — DeArrow's where it has one.
 *
 * The `href` keeps its `list=`, which matters for one of the two answers a press offers: **Play now**
 * discards the queue and opens the video, and opening a playlist entry means opening it *inside its
 * playlist*. **Play next** builds a queue row instead, whose route is the video id alone.
 */
export function playlistQueueCandidate(
  row: Extract<PlaylistEntryRow, { kind: 'video' }>,
  title: string,
): QueueCandidate {
  return {
    videoId: row.videoId,
    href: row.href,
    title,
    author: row.author,
    authorHref: row.authorHref,
    thumbnailUrl: row.thumbnailUrl,
    durationSec: row.durationSec,
  };
}

/**
 * `playlistId` is a parameter rather than something read off the listing, because a `PlaylistListing`
 * is folded from pages and genuinely does not know its own id — and an entry's `href` needs it, since
 * opening an entry has to keep the `list=` context.
 */
export function toPlaylistEntryRows(
  playlistId: string,
  listing: PlaylistListing,
  nowEpochSec: number,
  selected: ReadonlySet<string> = NO_KEYS,
  progress: ProgressLookup = NO_PROGRESS,
): readonly PlaylistEntryRow[] {
  // Across the *available* rows only, so a playlist that is mostly dead videos is not graded "this
  // response came back with titles only" (`playlistScrape`).
  const scrape = playlistScrape(listing.entries, nowEpochSec);

  return listing.entries.map((entry): PlaylistEntryRow => {
    const video = entry.video;
    const position = entry.index + 1;
    /*
     * **Keyed on `indexId`, and the React `key` is too.** 2e keyed rows on `entry:${index}` because
     * `index` was the identity of a row in a read-only list. It is not one any more: removing an entry
     * renumbers everything after it, so a key built from the position would make React re-use row 5's
     * DOM — including a half-open menu and an in-flight button state — for what is now a different
     * video. `indexId` is stable across a removal; a mirrored playlist has none, and there `index` is
     * as stable as it ever was because nothing can change the list.
     */
    const key = entry.indexId === null ? `entry:${entry.index}` : `entry:${entry.indexId}`;
    const isSelected = entry.indexId !== null && selected.has(entry.indexId);
    const selectLabel = text().playlist.manage.selectLabel(
      video.title.trim() === '' ? text().playlist.entry.positionLabel(position) : video.title,
    );

    if (playlistEntryUnavailable(video)) {
      return {
        kind: 'unavailable',
        key,
        position,
        videoId: video.videoId,
        indexId: entry.indexId,
        selected: isSelected,
        selectLabel,
      };
    }

    const facts = playlistEntryFacts(video, nowEpochSec, scrape);
    const route = channelRouteOf(video);
    const watched = progress(video.videoId);

    return {
      kind: 'video',
      key,
      position,
      videoId: video.videoId,
      indexId: entry.indexId,
      selected: isSelected,
      selectLabel,
      // Carries `list` and `index`, so opening an entry keeps the playlist context the reader came
      // from — the one case where a listing click is *not* a fresh video (`listing/model.ts` explains
      // why every other one is).
      href: formatRoute({
        kind: 'watch',
        videoId: video.videoId,
        startSec: null,
        playlistId,
        index: entry.index,
      }),
      title: video.title,
      author: playlistEntryAuthorMissing(video) ? null : video.author,
      authorHref: route === null ? null : formatRoute(route),
      thumbnailUrl:
        pickImage(video.thumbnails, { minWidth: ROW_THUMBNAIL_MIN_WIDTH, widescreen: true })?.url ?? null,
      badge: toBadge(facts.badge),
      // `0` is this endpoint's "not reported", and the clamp ignores a non-positive length anyway.
      durationSec: video.lengthSeconds > 0 ? video.lengthSeconds : null,
      // 4b — the bar, but no marker: a playlist card has no room for a word before the title and the
      // position number is already doing that job.
      progress: toListingProgress(watched.mark),
      watched: isWatchedVideo(watched),
    };
  });
}

const NO_KEYS: ReadonlySet<string> = new Set<string>();

/* ------------------------------------------------------------------------------------------------
 * The manage mode's add box — what one line of input means, and the candidates it produces
 * ---------------------------------------------------------------------------------------------- */

export type AddVideoQuery =
  | { readonly kind: 'empty' }
  /** A video id, bare or read out of a pasted watch URL — one details fetch away from a candidate. */
  | { readonly kind: 'video'; readonly videoId: string }
  /** Plain text: a video search. */
  | { readonly kind: 'search'; readonly q: string };

/**
 * The manage-subscriptions page's `classifyAddInput`, for videos: `parseRoute` does the reading, so a
 * full `youtube.com/watch?v=…`, a `youtu.be/…` link and a bare id all land in the same arm the router
 * uses. What does not read as a video is a search, never an error.
 */
export function classifyAddVideoInput(raw: string): AddVideoQuery {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'empty' };
  if (isVideoId(trimmed)) return { kind: 'video', videoId: trimmed };

  const route = parseRoute(trimmed);
  if (route.kind === 'watch') return { kind: 'video', videoId: route.videoId };

  return { kind: 'search', q: trimmed };
}

/** A video the add box found — a search hit or a resolved link — ready for an Add button. */
export interface VideoCandidateRow {
  readonly videoId: string;
  readonly title: string;
  readonly author: string | null;
  readonly thumbnailUrl: string | null;
  readonly durationText: string | null;
}

/** The smallest thumbnail still ≥ the row's box at 2× — these draw at 96 px wide. */
const CANDIDATE_THUMBNAIL_MIN_WIDTH = 192;

export function videoCandidateFromSummary(video: VideoSummary): VideoCandidateRow {
  return {
    videoId: video.videoId,
    title: video.title,
    author: video.author.trim() === '' ? null : video.author,
    thumbnailUrl:
      pickImage(video.thumbnails, { minWidth: CANDIDATE_THUMBNAIL_MIN_WIDTH, widescreen: true })?.url ??
      null,
    // Zero is refused for 2b's reason: on listings it means "not served", not "zero seconds long".
    durationText: video.lengthSeconds > 0 ? formatDuration(video.lengthSeconds) : null,
  };
}

export function videoCandidateFromDetails(video: VideoDetails): VideoCandidateRow {
  return {
    videoId: video.videoId,
    title: video.title,
    author: video.author.trim() === '' ? null : video.author,
    thumbnailUrl:
      pickImage(video.thumbnails, { minWidth: CANDIDATE_THUMBNAIL_MIN_WIDTH, widescreen: true })?.url ??
      null,
    durationText: video.lengthSeconds > 0 ? formatDuration(video.lengthSeconds) : null,
  };
}

/** The video rows out of a mixed search answer — whatever arrives, only videos are candidates. */
export function videoCandidatesFromSearch(items: readonly FeedItem[]): readonly VideoCandidateRow[] {
  return items.flatMap((item) => (item.kind === 'video' ? [videoCandidateFromSummary(item.video)] : []));
}

function toBadge(badge: ReturnType<typeof playlistEntryFacts>['badge']): EntryBadge | null {
  switch (badge.kind) {
    case 'duration':
      return { kind: 'duration', text: formatDuration(badge.seconds), title: null };
    case 'upcoming':
      return {
        kind: 'upcoming',
        text: text().playlist.entry.upcoming,
        title: fmt().absoluteDate(badge.premiereAtEpochSec),
      };
    case 'live':
    case 'unknown':
      // `live` is unreachable — `playlistEntryFacts` never returns it, because on this endpoint
      // `liveNow: true` means the video was deleted (559 of 559 rows). `unknown` draws nothing, which
      // is the honest answer for a row whose length the API did not give.
      return null;
  }
}

/* ------------------------------------------------------------------------------------------------
 * The page state
 * ---------------------------------------------------------------------------------------------- */

/**
 * **This page has no notices.** Four of the five were sentences about the response — rows with no
 * byline, no duration, rows that could not be read, a declared count that disagrees with the list —
 * and told the reader about our plumbing. The fifth counted the entries YouTube no longer serves, and
 * went with them (2026-08-07): each of those rows already carries `entryGone` on itself, in place and
 * beside the position number it accounts for, so the banner was a tally of what the reader could
 * already see.
 *
 * Core still computes all five (`playlistNotes`), and the tests that measure this endpoint still read
 * them — the notes are a fact about the payload, and only the rendering of them is gone.
 */

export type PlaylistScreenState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'problem'; readonly copy: ProblemCopy }
  | {
      readonly kind: 'empty';
      /** Carried since 3c: an editable empty playlist still needs its title and its controls. */
      readonly header: PlaylistHeader;
      readonly title: string;
      readonly detail: string;
    }
  | {
      readonly kind: 'ready';
      readonly header: PlaylistHeader;
      readonly rows: readonly PlaylistEntryRow[];
      /** The grid search emptied the page — a sentence, or an emptied grid reads as no entries. */
      readonly nothingMatches: boolean;
      /** `null` when the fold is finished — see `PlaylistListing.nextPage` for why size is not the signal. */
      readonly loadMore: 'available' | 'loading' | null;
      readonly loaded: number;
    };

export interface PlaylistStateOptions {
  readonly nowEpochSec: number;
  readonly loadingMore: boolean;
  /** A session exists to write with. Combined with `invidiousOwned` — see {@link PlaylistHeader.canEdit}. */
  readonly canEdit: boolean;
  /** Who is reading, so `playlistEditable` can tell your own native playlist from somebody else's. */
  readonly viewerUsername: string | null;
  /** The privacy this session set, which is the only exact source there is (`playlistVisibility`). */
  readonly knownPrivacy: PlaylistPrivacy | null;
  /** The manage selection, by `indexId` — empty outside manage mode. */
  readonly selected: ReadonlySet<string>;
  /** 4b's stored playheads (`useWatchProgressLookup`). */
  readonly progress: ProgressLookup;
  /**
   * Show the loaded entries last-first (`playlists/orderMemory.ts`). Display-only by force — the API
   * has no reorder endpoint (§7i) — so each row keeps its real position chip: entry 120 first, not a
   * renumbering pretending the playlist changed.
   */
  readonly reversed: boolean;
  /** The grid search — display-only over the loaded entries; positions stay original. */
  readonly search: string;
}

/**
 * The display-order menu, built here so `view.tsx` renders props — the search screen's rule
 * for its filter menus. Two entries only, and "Original" rather than "as added": the latter would be
 * a claim about mirrored playlists nothing verifies.
 */
export function playlistOrderMenu(reversed: boolean): MenuModel {
  const words = text().playlist.page.order;
  return {
    label: words.label,
    value: reversed ? words.reversed : words.normal,
    sections: [
      {
        title: null,
        items: [
          { id: 'normal', label: words.normal, detail: null, selected: !reversed },
          { id: 'reversed', label: words.reversed, detail: null, selected: reversed },
        ],
      },
    ],
    note: null,
  };
}

export function playlistScreenState(
  playlist: Playlist | null,
  listing: PlaylistListing | null,
  problem: ApiProblem | null,
  options: PlaylistStateOptions,
): PlaylistScreenState {
  if (problem !== null) return { kind: 'problem', copy: describeProblem(problem, playlistProblem()) };
  if (playlist === null || listing === null) return { kind: 'loading' };

  const header = toPlaylistHeader(playlist, listing, options.nowEpochSec, {
    canEdit: options.canEdit,
    viewerUsername: options.viewerUsername,
    knownPrivacy: options.knownPrivacy,
  });

  if (listing.entries.length === 0) {
    /*
     * Nothing to show *yet* is not an empty playlist. A reversed read's first response is the
     * header-only probe page, and a direction flipped over pages fetched from the other end leaves
     * the run empty until the walk reaches back to them — in both, a page is already on its way.
     */
    if (listing.nextPage !== null) return { kind: 'loading' };
    /*
     * `empty` carries the header now, where 2e's did not. An editable playlist that is empty is the
     * *normal* state right after creating one, and a page that answers a create with nothing but "this
     * playlist has no videos" has thrown away the title, the visibility and every control — including
     * the one for deleting a playlist made by accident.
     */
    const words = text().playlist.empty;
    return {
      kind: 'empty',
      header,
      title: words.title,
      detail: playlist.invidiousOwned ? words.owned : words.mirrored,
    };
  }

  const all = toPlaylistEntryRows(
    playlist.playlistId,
    listing,
    options.nowEpochSec,
    options.selected,
    options.progress,
  );
  // A dead entry has no title to match, so a search hides it too — its position chip survives on
  // the rows that stay, so the numbering keeps pointing at the real playlist.
  const rows = all.filter((row) =>
    matchesGridSearch(
      options.search,
      row.kind === 'video' ? row.title : null,
      row.kind === 'video' ? row.author : null,
    ),
  );

  return {
    kind: 'ready',
    header,
    rows: options.reversed ? [...rows].reverse() : rows,
    nothingMatches: all.length > 0 && rows.length === 0,
    loadMore: listing.nextPage === null ? null : options.loadingMore ? 'loading' : 'available',
    loaded: listing.entries.length,
  };
}

/**
 * The two sentences that are genuinely about a playlist; `api/problem.ts` writes the other seven once.
 *
 * `notFound` covers **three different 404 bodies** that all mean the same thing — a relayed YouTube 404
 * for an unknown `PL…` (and for `WL`/`LL`, which need an account), `Could not extract
 * playlistSidebarRenderer.` for something that is not a playlist id at all, and `Playlist does not
 * exist.` for an unknown `IVPL…`. The first of those used to classify as `upstream-broken`, which is
 * what `relayedUpstreamStatus` was added for.
 *
 * `upstreamBroken` is supplied because it has a reachable cause here, exactly as it does for a channel:
 * a YouTube **mix** id (`RDCLAK5uy_…`) answers `500` with a leaked `KeyError`, and a mix reached from a
 * search result is a link a reader can really click.
 */
function playlistProblem(): {
  readonly notFound: ProblemCopy;
  readonly unreadable: (error: { entity: string; field: string | null; reason: string }) => ProblemCopy;
  readonly upstreamBroken: ProblemCopy;
} {
  const words = text().playlist.problem;
  return {
    notFound: words.notFound,
    unreadable: () => words.unreadable,
    upstreamBroken: words.upstreamBroken,
  };
}

export const playlistProblemSubject = playlistProblem;
